import { type FC, useTransition, useState, useMemo, useCallback, useEffect } from 'react';
import { graphql, useRefetchableFragment, useSubscription, useFragment } from 'react-relay';

import { defineMessages, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft.ts';

import { NftProfileImportCollectionViewNftList } from './NftProfileImportCollectionViewNftList.tsx';
import { useRouteClickHandler } from '../useRouteClickHandler.ts';
import { useNftProfileOwner } from '../../../../helpers/useNftProfileOwner.tsx';
import { NftPublicProfileAddressBadge } from '../../../NftPublicProfileAddressBadge/NftPublicProfileAddressBadge.tsx';
import { NftProfileImportCollectionViewSpinner } from './NftProfileImportCollectionViewSpinner.tsx';
import { NftProfileImportCollectionModalLazy } from './NftProfileImportCollectionModalLazy.tsx';
import { paths } from '../../../../constants.ts';
import { NftProfileImportCollectionViewToolbar } from './NftProfileImportCollectionViewToolbar.tsx';

import { type NftProfileImportCollectionView_viewer$key as viewer$key } from './__generated__/NftProfileImportCollectionView_viewer.graphql.ts';
import { type NftProfileImportCollectionView_contractImport$key as contractImport$key } from './__generated__/NftProfileImportCollectionView_contractImport.graphql.ts';
import { type NftProfileImportCollectionViewRefetchQuery as RefetchQuery } from './__generated__/NftProfileImportCollectionViewRefetchQuery.graphql.ts';

import styles from './styles/NftProfileImportCollectionView.scss';

const BACK_RELATIVE_PATH = '..';

const messages = defineMessages({
    back: {
        id: 'nftProfile.importCollectionView.backNav',
        defaultMessage: 'Back to all Creations',
    },
    errors: { id: 'nftProfile.importCollectionView.errors', defaultMessage: 'Import Errors' },
});

const subscription = graphql`
    subscription NftProfileImportCollectionViewSubscription(
        $tokenContractId: String!
        $userId: String!
        $fetchUser: Boolean = true
    ) {
        nftTokenContractImportUpdate(input: { tokenContractId: $tokenContractId }) {
            nftTokenContractImport {
                ...NftProfileImportCollectionView_contractImport
                ...NftProfileImportCollectionViewNftList_contractImport
                    @arguments(
                        importStatuses: [
                            NFT_ITEM_IMPORT_STATUS_COMPLETE
                            NFT_ITEM_IMPORT_STATUS_PROCESSING
                            NFT_ITEM_IMPORT_STATUS_ERROR
                        ]
                    )
            }
        }
    }
`;

const refetchFragment = graphql`
    fragment NftProfileImportCollectionView_viewer on Viewer
    @refetchable(queryName: "NftProfileImportCollectionViewRefetchQuery") {
        nftTokenContractImportErrors: nftTokenContractImportById(tokenContractId: $tokenContractId)
            @include(if: $hasTokenContractId) {
            ...NftProfileImportCollectionViewNftList_contractImport
                @arguments(importStatuses: [NFT_ITEM_IMPORT_STATUS_ERROR])
        }
        nftTokenContractImportById(tokenContractId: $tokenContractId)
            @include(if: $hasTokenContractId) {
            errorItems: items(first: 0, importStatuses: [NFT_ITEM_IMPORT_STATUS_ERROR]) {
                totalResults
            }
            ...NftProfileImportCollectionViewNftList_contractImport
            ...NftProfileImportCollectionView_contractImport
        }
        ...NftProfileImportCollectionViewNftList_viewer
        publicProfile(username: $username) {
            username
        }
        ...NftProfileImportCollectionViewToolbar_viewer
        ...NftProfileImportCollectionModal_viewer
    }
`;
const contractFragment = graphql`
    fragment NftProfileImportCollectionView_contractImport on NftTokenContractImport {
        ...NftProfileImportCollectionModal_contractImport
        ...NftProfileImportCollectionViewToolbar_contractImport
        serviceId
        contractAddress {
            address
        }
        collectionName
        description: collectionDescription
        contract {
            address
            name
            symbol
        }
    }
`;

export const NftProfileImportCollectionView: FC<{
    viewer: viewer$key;
    showBackNav?: boolean;
    hasTitleLink?: boolean;
    tokenContractId?: string;
}> = ({ viewer: viewerRef, showBackNav, hasTitleLink, tokenContractId = '' }) => {
    const { formatMessage } = useIntl();
    const { isProfileOwner, canImport } = useNftProfileOwner();
    const [viewer, refetch] = useRefetchableFragment<RefetchQuery, viewer$key>(
        refetchFragment,
        viewerRef
    );
    const contractImport = useFragment<contractImport$key>(
        contractFragment,
        viewer.nftTokenContractImportById || null
    );
    useSubscription(
        useMemo(
            () => ({ variables: { tokenContractId, userId: '', fetchUser: false }, subscription }),
            [tokenContractId]
        )
    );

    const handleBackNavClick = useRouteClickHandler(BACK_RELATIVE_PATH);
    const maybeHandleTitleClick = useRouteClickHandler(contractImport?.serviceId || '');
    const handleTitleClick = contractImport?.serviceId ? maybeHandleTitleClick : () => {};

    const collectionUrl =
        viewer.publicProfile?.username && contractImport?.serviceId
            ? `${paths.profileUser}${viewer.publicProfile.username}/${contractImport.serviceId}/`
            : undefined;

    const [isPending, startTransition] = useTransition();

    const refetchCollectionCallback = useCallback(() => {
        startTransition(() => {
            refetch({}, { fetchPolicy: 'network-only' });
        });
    }, [refetch, startTransition]);

    useEffect(() => {
        if (contractImport?.serviceId !== tokenContractId) {
            startTransition(() => {
                refetch({ tokenContractId, hasTokenContractId: !!tokenContractId });
            });
        }
    }, [refetch, tokenContractId, startTransition, contractImport]);

    const { contractAddress, contract, description, collectionName } = contractImport || {};
    const username = viewer.publicProfile?.username;
    const [title, subtitle] = collectionName ? [collectionName, contract?.name] : [contract?.name];
    const showErrors =
        isProfileOwner && !!viewer.nftTokenContractImportById?.errorItems?.totalResults;

    const [isEditOpen, setIsEditOpen] = useState(false);

    return (
        <>
            {showBackNav ? (
                <Link
                    className={styles.backLink}
                    buyerLinkType="linkNoDecoration"
                    onClick={handleBackNavClick}
                    href={username ? `${paths.profileUser}${username}` : '/'}
                >
                    <ArrowLeft className={styles.icon} /> {formatMessage(messages.back)}
                </Link>
            ) : null}
            <div className={styles.manifoldCollectionContainer}>
                <NftProfileImportCollectionViewSpinner isOpen={isPending} />
                {!isPending ? (
                    <>
                        <h2 className={styles.heading}>
                            {hasTitleLink ? (
                                <Link
                                    href={collectionUrl}
                                    onClick={handleTitleClick}
                                    buyerLinkType="linkNoDecoration"
                                >
                                    {title}
                                </Link>
                            ) : (
                                title
                            )}
                        </h2>
                        <div className={styles.info}>
                            <span className={styles.text}>{subtitle}</span>
                            <span className={styles.text}>{contract?.symbol}</span>
                            <NftPublicProfileAddressBadge
                                walletAddress={contractAddress?.address}
                                size="small-regular"
                            />
                            {isProfileOwner && (
                                <NftProfileImportCollectionViewToolbar
                                    onEditClick={() => setIsEditOpen(true)}
                                    contractImport={contractImport}
                                    viewer={viewer}
                                />
                            )}
                        </div>
                        <p className={styles.description}>{description}</p>
                        <NftProfileImportCollectionViewNftList
                            contractImport={viewer.nftTokenContractImportById}
                            viewer={viewer}
                        />
                    </>
                ) : null}
                {!isPending && showErrors ? (
                    <>
                        <h2 className={styles.heading}>{formatMessage(messages.errors)}</h2>
                        <NftProfileImportCollectionViewNftList
                            contractImport={viewer.nftTokenContractImportErrors}
                            viewer={viewer}
                            refetchCollection={refetchCollectionCallback}
                        />
                    </>
                ) : null}
            </div>
            {canImport && (
                <NftProfileImportCollectionModalLazy
                    contractImport={contractImport}
                    viewer={viewer}
                    setOpen={setIsEditOpen}
                    isOpen={isEditOpen}
                />
            )}
        </>
    );
};
